export const gridDefaults = {
    navigation: 3, // it's a flag: 0 = none, 1 = top, 2 = bottom, 3 = both (top and bottom)
    padding: 2, // page padding (pagination)
    columnSelection: true,
    rowCount: [10, 25, 50, -1], // rows per page int or array of int (-1 represents "All")
    refreshIconVisible: true,
    columnSelectionVisible: true,

    //TWINVISION
    //Added to support column searches
    twinvisionColumnSearch: false,
    twinvisionColumnSearchCollapsed: true,
    twinvisionColumnSearchExclusions: [],
    twinvisionColumnSearchValues: {},
    twinvisionPreventInitialPost: false,
    /**
     * Enables row selection (to enable multi selection see also `multiSelect`). Default value is `false`.
     *
     * @property selection
     * @type Boolean
     * @default false
     * @for defaults
     * @since 1.0.0
     **/
    selection: false,

    /**
     * Enables multi selection (`selection` must be set to `true` as well). Default value is `false`.
     *
     * @property multiSelect
     * @type Boolean
     * @default false
     * @for defaults
     * @since 1.0.0
     **/
    multiSelect: false,

    isSelectAllCheckboxVisible: true,

    /**
     * Enables entire row click selection (`selection` must be set to `true` as well). Default value is `false`.
     *
     * @property rowSelect
     * @type Boolean
     * @default false
     * @for defaults
     * @since 1.1.0
     **/
    rowSelect: false,

    /**
     * Defines whether the row selection is saved internally on filtering, paging and sorting
     * (even if the selected rows are not visible).
     *
     * @property keepSelection
     * @type Boolean
     * @default false
     * @for defaults
     * @since 1.1.0
     **/
    keepSelection: false,

    highlightRows: false, // highlights new rows (find the page of the first new row)
    sorting: true,
    multiSort: false,

    /**
     * General search settings to configure the search field behaviour.
     *
     * @property searchSettings
     * @type Object
     * @for defaults
     * @since 1.2.0
     **/
    searchSettings: {
        /**
         * The time in milliseconds to wait before search gets executed.
         *
         * @property delay
         * @type Number
         * @default 250
         * @for searchSettings
         **/
        delay: 250,

        /**
         * The characters to type before the search gets executed.
         *
         * @property characters
         * @type Number
         * @default 1
         * @for searchSettings
         **/
        characters: 1,
    },

    /**
     * Defines whether the data shall be loaded via an asynchronous HTTP (Ajax) request.
     *
     * @property ajax
     * @type Boolean
     * @default false
     * @for defaults
     **/
    ajax: false,

    /**
     * Ajax request settings that shall be used for server-side communication.
     * All setting except data, error, success and url can be overridden.
     * For the full list of settings go to http://api.jquery.com/jQuery.ajax/.
     *
     * @property ajaxSettings
     * @type Object
     * @for defaults
     * @since 1.2.0
     **/
    ajaxSettings: {
        /**
         * Specifies the HTTP method which shall be used when sending data to the server.
         * Go to http://api.jquery.com/jQuery.ajax/ for more details.
         * This setting is overriden for backward compatibility.
         *
         * @property method
         * @type String
         * @default "POST"
         * @for ajaxSettings
         **/
        method: "POST",
    },

    /**
     * Enriches the request object with additional properties. Either a `PlainObject` or a `Function`
     * that returns a `PlainObject` can be passed. Default value is `{}`.
     *
     * @property post
     * @type Object|Function
     * @default function (request) { return request; }
     * @for defaults
     * @deprecated Use instead `requestHandler`
     **/
    post: {}, // or use function () { return {}; } (reserved properties are "current", "rowCount", "sort" and "searchPhrase")

    /**
     * Sets the data URL to a data service (e.g. a REST service). Either a `String` or a `Function`
     * that returns a `String` can be passed. Default value is `""`.
     *
     * @property url
     * @type String|Function
     * @default ""
     * @for defaults
     **/
    url: "", // or use function () { return ""; }

    /**
     * Defines whether the search is case sensitive or insensitive.
     *
     * @property caseSensitive
     * @type Boolean
     * @default true
     * @for defaults
     * @since 1.1.0
     **/
    caseSensitive: true,

    // note: The following properties should not be used via data-api attributes
    /**
     * Transforms the JSON request object in what ever is needed on the server-side implementation.
     *
     * @property requestHandler
     * @type Function
     * @default function (request) { return request; }
     * @for defaults
     * @since 1.1.0
     **/
    requestHandler: function (request) {
        return request;
    },

    /**
     * Transforms the response object into the expected JSON response object.
     *
     * @property responseHandler
     * @type Function
     * @default function (response) { return response; }
     * @for defaults
     * @since 1.1.0
     **/
    responseHandler: function (response) {
        return response;
    },

    /**
     * A list of converters.
     *
     * @property converters
     * @type Object
     * @for defaults
     * @since 1.0.0
     **/
    converters: {
        numeric: {
            from: function (value) {
                return +value;
            }, // converts from string to numeric
            to: function (value) {
                return value + "";
            }, // converts from numeric to string
        },
        string: {
            // default converter
            from: function (value) {
                return value;
            },
            to: function (value) {
                return value;
            },
            // Natural sort: compares embedded numeric runs numerically (e.g. "DI 2" < "DI 10")
            // instead of lexicographically, regardless of the surrounding text/prefix.
            sort: function (a, b) {
                let reChunk = /(\d+)|(\D+)/g,
                    aParts = String(a ?? "").match(reChunk) || [],
                    bParts = String(b ?? "").match(reChunk) || [],
                    length = Math.max(aParts.length, bParts.length);

                for (let i = 0; i < length; i++) {
                    let aPart = aParts[i] || "",
                        bPart = bParts[i] || "",
                        aNum = Number(aPart),
                        bNum = Number(bPart);

                    if (aPart !== "" && bPart !== "" && !isNaN(aNum) && !isNaN(bNum)) {
                        if (aNum !== bNum) return aNum < bNum ? -1 : 1;
                    } else if (aPart !== bPart) {
                        return aPart < bPart ? -1 : 1;
                    }
                }
                return 0;
            },
        },
    },

    /**
     * Contains all css classes.
     *
     * @property css
     * @type Object
     * @for defaults
     **/
    css: {
        actions: "actions btn-group", // must be a unique class name or constellation of class names within the header and footer
        center: "text-center",
        columnHeaderAnchor: "column-header-anchor sortable d-flex align-items-center gap-2 justify-content-between", // must be a unique class name or constellation of class names within the column header cell
        columnHeaderText: "text",
        dropDownItem: "dropdown-item", // must be a unique class name or constellation of class names within the actionDropDown,
        dropDownItemButton: "dropdown-item-button", // must be a unique class name or constellation of class names within the actionDropDown
        dropDownItemCheckbox: "dropdown-item-checkbox", // must be a unique class name or constellation of class names within the actionDropDown
        dropDownMenu: "dropdown btn-group", // must be a unique class name or constellation of class names within the actionDropDown
        dropDownMenuItems: "dropdown-menu pull-right", // must be a unique class name or constellation of class names within the actionDropDown
        dropDownMenuText: "dropdown-text", // must be a unique class name or constellation of class names within the actionDropDown
        footer: "bootgrid-footer my-3 px-3 d-flex justify-content-between",
        header: "bootgrid-header my-3 px-3 d-flex gap-1 actionBar justify-content-between",
        icon: "fa",
        iconColumns: "fa fa-list",
        iconDown: "fa-chevron-down",
        iconRefresh: "fa fa-refresh",
        iconSearch: "fa fa-search",
        iconUp: "fa-chevron-up",
        iconColumnSearchToggle: "iconcolumnplaceholder",
        iconColumnSearchToggleCollapsed: "glyphicon glyphicon-zoom-in",
        iconColumnSearchToggleUncollapsed: "glyphicon glyphicon-zoom-out",
        infos: "infos", // must be a unique class name or constellation of class names within the header and footer,
        left: "text-left",
        pagination: "pagination m-0 d-flex", // must be a unique class name or constellation of class names within the header and footer
        paginationButton: "page-link", // must be a unique class name or constellation of class names within the pagination
        columnSearch: "form-control columnsearchfield",
        columnSearchToggle: "btn btn-outline-secondary d-flex gap-1 align-items-center search-collapse",

        /**
         * CSS class to select the parent div which activates responsive mode.
         *
         * @property responsiveTable
         * @type String
         * @default "table-responsive"
         * @for css
         * @since 1.1.0
         **/
        responsiveTable: "table-responsive",

        right: "text-right",
        search: "search form-group", // must be a unique class name or constellation of class names within the header and footer
        searchField: "search-field form-control",
        selectBox: "select-box", // must be a unique class name or constellation of class names within the entire table
        selectCell: "select-cell", // must be a unique class name or constellation of class names within the entire table

        /**
         * CSS class to highlight selected rows.
         *
         * @property selected
         * @type String
         * @default "active"
         * @for css
         * @since 1.1.0
         **/
        selected: "row_selected info",

        sortable: "sortable",
        table: "bootgrid-table table",
    },

    /**
     * A dictionary of formatters.
     *
     * @property formatters
     * @type Object
     * @for defaults
     * @since 1.0.0
     **/
    formatters: {},

    /**
     * Contains all labels.
     *
     * @property labels
     * @type Object
     * @for defaults
     **/
    labels: {
        all: "All",
        infos: "Showing {{ctx.start}} to {{ctx.end}} of {{ctx.total}} entries",
        loading: "Loading...",
        noResults: "No results found!",
        refresh: "Refresh",
        search: "Search",
    },

    /**
     * Specifies the mapping between status and contextual classes to color rows.
     *
     * @property statusMapping
     * @type Object
     * @for defaults
     * @since 1.2.0
     **/
    statusMapping: {
        /**
         * Specifies a successful or positive action.
         *
         * @property 0
         * @type String
         * @for statusMapping
         **/
        0: "success",

        /**
         * Specifies a neutral informative change or action.
         *
         * @property 1
         * @type String
         * @for statusMapping
         **/
        1: "info",

        /**
         * Specifies a warning that might need attention.
         *
         * @property 2
         * @type String
         * @for statusMapping
         **/
        2: "warning",

        /**
         * Specifies a dangerous or potentially negative action.
         *
         * @property 3
         * @type String
         * @for statusMapping
         **/
        3: "danger",
    },

    /**
     * Contains all templates.
     *
     * @property templates
     * @type Object
     * @for defaults
     **/
    templates: {
        actionButton: '<button class="btn btn-outline-secondary" type="button" title="{{ctx.text}}">{{ctx.content}}</button>',
        actionDropDown:
            `<div class="{{css.dropDownMenu}}">
                <button class="btn btn-outline-secondary dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                    <span class="{{css.dropDownMenuText}}">{{ctx.content}}</span>
                    <span class="caret"></span>
                </button>
                <ul class="{{css.dropDownMenuItems}}" style = "max-height: 200px; overflow-y: scroll;" role="menu"></ul>
            </div>`,
        actionDropDownItem: '<li><a href="{{ctx.uri}}" class="{{css.dropDownItem}} {{css.dropDownItemButton}}">{{ctx.text}}</a></li>',
        actionDropDownCheckboxItem:
            '<li><label class="{{css.dropDownItem}}"><input name="{{ctx.name}}" type="checkbox" value="1" class="{{css.dropDownItemCheckbox}}" {{ctx.checked}} /> {{ctx.label}}</label></li>',
        actions: '<div class="{{css.actions}}"></div>',
        body: "<tbody></tbody>",
        cell: '<td class="{{ctx.css}}" {{ctx.data}} style="{{ctx.style}}">{{ctx.content}}</td>',
        footer: `
        <div id="{{ctx.id}}" class="{{css.footer}}">
            <nav>
                <p class="{{css.pagination}}"></p>
            </nav>
            <p class="{{css.infos}}"></p>
        </div> `,

        header:`
        <div id="{{ctx.id}}" class="{{css.header}}">
            <div class="actionBar-left"></div>
            <div class="actionBar-right"></div>
        </div>`,

        headerCell:`
        <th draggable="true" data-column-id="{{ctx.column.id}}" data-type="{{ctx.column.dataType}}" class="{{ctx.css}}" style="{{ctx.style}}">
            <a href="javascript:void(0);" class="{{css.columnHeaderAnchor}} {{ctx.sortable}}">
                <span class="{{css.columnHeaderText}}">{{ctx.column.text}}</span>{{ctx.icon}}
            </a>
        </th>`,

        icon: '<i class="{{css.icon}} {{ctx.iconCss}}"aria-hidden="true"></i>',
        infos: '<div class="{{css.infos}}">{{lbl.infos}}</div>',
        loading: '<tr><td colspan="{{ctx.columns}}" class="loading">{{lbl.loading}}</td></tr>',
        noResults: '<tr><td colspan="{{ctx.columns}}" class="no-results">{{lbl.noResults}}</td></tr>',
        pagination: '<ul class="{{css.pagination}}"></ul>',
        paginationItem: `
        <li class="{{ctx.css}}">
            <a href="{{ctx.uri}}" class="{{css.paginationButton}}">
                {{ctx.text}}
            </a>
        </li>`,
        rawHeaderCell: '<th class="{{ctx.css}}">{{ctx.content}}</th>', // Used for the multi select box
        row: "<tr{{ctx.attr}}>{{ctx.cells}}</tr>",
        search: '<div class="{{css.search}}"><div class="input-group"><span class="{{css.icon}} input-group-addon {{css.iconSearch}}"></span> <input type="text" class="{{css.searchField}}" placeholder="{{lbl.search}}" /></div></div>',
        columnSearch: '<input type="text" value="" class="{{css.columnSearch}}" placeholder="{{lbl.search}}">',
        columnSearchToggle: '<button type="button" class="{{css.columnSearchToggle}}"><span class="{{css.iconColumnSearchToggle}}"></span></button>',
        select: '<input name="select" type="{{ctx.type}}" class="{{css.selectBox}}" value="{{ctx.value}}" {{ctx.checked}} />',
    },
};
